import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Bell,
  Search,
  Globe,
  ChevronDown,
  LogOut,
  User,
  Menu,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { PosStatusBadge } from '@/components/PosStatusBadge'
import { BotStatusBadge } from '@/components/BotStatusBadge'

// Page titles mapping
const pageTitles: Record<string, string> = {
  '/': 'Dashboard',
  '/sales': 'Sales & Income',
  '/inventory': 'Inventory Management',
  '/inventory-tasks': 'Inventory Tasks',
  '/staff': 'Staff Management',
  '/salary': 'Salary Management',
  '/attendance': 'Attendance',
  '/reports': 'Reports',
  '/notifications': 'Notifications',
  '/activity': 'Activity Log',
  '/system': 'System Health',
  '/announcements': 'Announcements',
  '/calendar': 'Calendar',
  '/goals': 'Goals & KPIs',
  '/prescription-calculator': 'Prescription Calculator',
  '/settings': 'Settings',
}

export default function Header({ onMenuClick }: { onMenuClick?: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { staffMember, signOut } = useAuthStore()
  const [showUserMenu, setShowUserMenu] = useState(false)

  const pageTitle = pageTitles[location.pathname] || 'Dashboard'

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  // Get user initials for avatar
  const getUserInitials = () => {
    if (staffMember?.first_name && staffMember?.last_name) {
      return `${staffMember.first_name[0]}${staffMember.last_name[0]}`.toUpperCase()
    }
    return 'U'
  }

  const getUserDisplayName = () => {
    if (staffMember?.first_name) {
      return staffMember.first_name
    }
    return 'User'
  }

  return (
    <header className="flex items-center justify-between h-16 px-4 lg:px-6 bg-white border-b border-slate-200">
      {/* Left side - Hamburger (mobile + landscape) + Page title */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6 text-slate-700" />
        </button>
        <h1 className="text-lg lg:text-xl font-semibold text-slate-900 truncate">{pageTitle}</h1>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2 lg:gap-4">
        {/* Status Badges */}
        <PosStatusBadge />
        <BotStatusBadge />

        {/* Search */}
        <div className="relative hidden lg:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search..."
            className="w-64 pl-10 pr-4 py-2 bg-slate-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        {/* Notifications */}
        <button
          onClick={() => navigate('/notifications')}
          className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-danger rounded-full" />
        </button>

        {/* Language toggle */}
        <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <Globe className="w-5 h-5 text-slate-600" />
        </button>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <span className="text-white text-sm font-medium">{getUserInitials()}</span>
            </div>
            <span className="hidden lg:block text-sm font-medium text-slate-700">{getUserDisplayName()}</span>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>

          {/* Dropdown menu */}
          {showUserMenu && (
            <>
              {/* Backdrop to close menu */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                <button
                  onClick={() => {
                    navigate('/settings')
                    setShowUserMenu(false)
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                >
                  <User className="w-4 h-4" />
                  Profile
                </button>
                <hr className="my-1 border-slate-200" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-danger hover:bg-slate-100"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
